import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  getValue,
  isDefined,
  isDefinedAndFilledArray,
} from '../../../../shared/utility/verify.lib';
import { Store } from '@ngrx/store';
import {
  availableLanguages,
  selectedLanguages,
} from '../../../../redux/mydoc.selector';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { cloneDeep } from 'lodash';
import { MatOptionSelectionChange } from '@angular/material/core';
import { setSelectedLanguages } from '../../../../redux/mydoc.action';
import { LanguageListHelperService } from '../../services/language-list-helper/language-list-helper.service';
import { SubscriptionComponent } from '../../../../shared/subscription/subscription.component';
import { ILanguageText } from '../../../../models/language/language-text.model';

@Component({
  selector: 'app-language-selection',
  templateUrl: './language-selection.component.html',
  styleUrl: './language-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectionComponent extends SubscriptionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public activeLanguageId: string = 'EN'; 

  @ViewChild('languageInput') public searchInput: ElementRef;
  @ViewChild('languageInput', { read: MatAutocompleteTrigger })
  public autocompleteOverlay: MatAutocompleteTrigger;

  public separatorKeysCodes: number[] = [ENTER];
  public selectedLanguagesList: string[] = [];

  public listOfPossibleLanguages: ILanguageText[] = [];
  public availableLanguages$ = this.store.select(availableLanguages);
  public selectedLanguages$ = this.store.select(selectedLanguages);
  public isLanguageSelected: Record<string, boolean> = {};
  public additionalChipCountText: string = null;
  public maxChipCount: number = 2;
  
  @ViewChild('languageSelection') private languageSelection: ElementRef;
  private languagesList: ILanguageText[] = [];
  private isOptionClickEvent: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private backupOverlay: MatAutocompleteTrigger = null;

  public constructor(
    private store: Store,
    private languageListHelper: LanguageListHelperService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.availableLanguages$
      .pipe(takeUntil(this.completeAll$))
      .subscribe((languageTexts: ILanguageText[]) => {
        this.languagesList = languageTexts;
        this.preselectFirstLanguages();
      });

    this.selectedLanguages$
      .pipe(takeUntil(this.completeAll$))
      .subscribe((languageTexts: ILanguageText[]) => {
        if (isDefinedAndFilledArray(() => languageTexts)) {
          this.selectedLanguagesList = languageTexts.map(
            (language: ILanguageText) => language.text
          );
          this.calculateAdditionalChipCountText();
        } else {
          this.selectedLanguagesList = [];
          this.additionalChipCountText = null;
        }
      });
  }

  public onClickOutside(): void {
    // when click outside was just a selection in the autocomplete we must skip this
    if (!this.isOptionClickEvent.getValue()) {
      this.simulateClickOutside();
    } else {
      this.resetClickEvent();
    }
  }

  public ngAfterViewInit(): void {
    this.backupOverlay = cloneDeep(this.autocompleteOverlay);
      this.autocompleteOverlay.closePanel = () => {
          // when close event was just a selection in the autocomplete we must skip this
          if (this.isOptionClickEvent.getValue()) {
            this.resetClickEvent();
          } else {
            this.backupOverlay.closePanel();
          }
        };
      
        setTimeout(() => {
          const languageSelectionPixelWidth: number = getValue(() => this.languageSelection.nativeElement.offsetWidth, 0);
          if (languageSelectionPixelWidth < 540) {
            this.maxChipCount = 1;
          }
        }, 500)
  }

  public onInputClick(): void {
    this.listOfPossibleLanguages = this.languageListHelper.sortLanguageList(this.languagesList, this.activeLanguageId);
    this.autocompleteOverlay.openPanel();
    this.searchInput.nativeElement.focus();

    // when view is not ready to open then we try again
    setTimeout(() =>  this.autocompleteOverlay.openPanel(), 300);
  }

  public onInputChange(): void {
    const searchText: string = this.searchInput?.nativeElement?.value;
    this.listOfPossibleLanguages = this.languageListHelper.filterLanguagesWithSearch(
      this.languageListHelper.sortLanguageList(this.languagesList, this.activeLanguageId), searchText
    );

    // when list is not open because it was empty, then open it again with new filtered entries
    if (isDefined(() => this.autocompleteOverlay.autocomplete.isOpen) && !this.autocompleteOverlay.autocomplete.isOpen) {
      this.searchInput.nativeElement.focus();
      setTimeout(() =>  this.autocompleteOverlay.openPanel(), 300);
    }
  }

  public onCloseClick(): void {
    this.languagesList.forEach(
      (language: ILanguageText) =>
        (this.isLanguageSelected[language.languageId] = false)
    );
    this.store.dispatch(setSelectedLanguages({ languages: [] }));
    this.resetSearchInput();
  }

  public addChip(event: MatChipInputEvent): void {
    const textToAdd: string = getValue(() => event.value, null);
    const foundLanguageText: ILanguageText = this.languagesList.find(
      (ILanguageText: ILanguageText) => ILanguageText.text === textToAdd
    );

    if (
      isDefined(() => foundLanguageText) &&
      !this.isLanguageSelected[foundLanguageText?.languageId]
    ) {
      this.isLanguageSelected[foundLanguageText?.languageId] = true;
      this.store.dispatch(
        setSelectedLanguages({
          languages: this.languageListHelper.getSelectedLanguageTextsFromRecord(this.getSelectedIdsSorting(), this.languagesList),
        })
      );
    }
    this.resetSearchInput();
  }

  public removeChip(chipToRemove: string): void {
    const selectedChipTexts: ILanguageText[] =
      this.getLatestValueFromSubscription(this.selectedLanguages$);

    if (isDefinedAndFilledArray(() => selectedChipTexts)) {
      const selectedChips: ILanguageText[] = selectedChipTexts.filter(
        (chipText: ILanguageText) => chipText.text !== chipToRemove
      );
      const newLanguageIds: string[] = selectedChips.map(
        (language: ILanguageText) => language.languageId
      );
      Object.keys(this.isLanguageSelected).forEach((key: string) => {
        this.isLanguageSelected[key] = newLanguageIds.includes(key);
      });
      this.store.dispatch(setSelectedLanguages({ languages: selectedChips }));
    }
  }

  public checkLanguage(event: MatOptionSelectionChange): void {
    this.isOptionClickEvent.next(true);
    const checkedLanguage: ILanguageText = getValue(
      () => event.source.value,
      null
    );
    this.isLanguageSelected[checkedLanguage.languageId] =
      !this.isLanguageSelected[checkedLanguage.languageId];
    this.store.dispatch(
      setSelectedLanguages({
        languages: this.languageListHelper.getSelectedLanguageTextsFromRecord(this.getSelectedIdsSorting(), this.languagesList),
      })
    );
    this.onInputClick();
    this.resetSearchInput();
  }

  public onIconClick(): void {
    if (getValue(() => this.listOfPossibleLanguages.length, 0) > 0) {
      this.onClickOutside();
    } else {
      this.onInputClick();
      this.isOptionClickEvent.next(true);
      this.resetClickEvent();
    }
  }

  private getSelectedIdsSorting(): string[] {
    let sortedOldSelectedIds: string[] = [];
    const selectedLanguages: ILanguageText[] = this.getLatestValueFromSubscription(this.selectedLanguages$);

    if (isDefinedAndFilledArray(() => selectedLanguages)) {
      sortedOldSelectedIds = selectedLanguages.map((language: ILanguageText) => language.languageId);
    }

    return this.languageListHelper.sortByAlreadySelectedLanguages(this.isLanguageSelected, sortedOldSelectedIds);
  }

  private preselectFirstLanguages(): void {
    if (
      isDefinedAndFilledArray(() => this.languagesList) &&
      Object.keys(this.isLanguageSelected).length === 0
    ) {
      this.listOfPossibleLanguages = this.languageListHelper.sortLanguageList(this.languagesList, this.activeLanguageId);
      const numberOfLanguagesToSelect: number = this.activeLanguageId === 'EN' ? 1 : 2;
      const languagesToSelect: ILanguageText[] = this.listOfPossibleLanguages.slice(0, numberOfLanguagesToSelect);
      const languagesToSelectIds: string[] = isDefinedAndFilledArray(() => languagesToSelect) ? languagesToSelect.map((language: ILanguageText) => language.languageId) : [];

      this.languagesList.forEach(
        (language: ILanguageText) =>
        {
          const languageId: string = getValue(() => language.languageId, null);
          this.isLanguageSelected[languageId] = languagesToSelectIds.includes(languageId); 
        }
      );
      this.store.dispatch(
        setSelectedLanguages({
          languages: languagesToSelect,
        })
      );
    }
    
  }

  private calculateAdditionalChipCountText(): void {
    if (this.selectedLanguagesList.length > this.maxChipCount) {
      this.additionalChipCountText = '+' + (this.selectedLanguagesList.length - this.maxChipCount);
    } else {
      this.additionalChipCountText = null;
    }
  }

  private resetClickEvent(): void {
    setTimeout(() => this.isOptionClickEvent.next(false), 500);
  }

  private resetSearchInput(): void {
    this.searchInput.nativeElement.value = '';
    this.listOfPossibleLanguages = this.languageListHelper.sortLanguageList(this.languagesList, this.activeLanguageId);
  }

  private simulateClickOutside(): void {
    this.listOfPossibleLanguages = [];
    this.autocompleteOverlay.closePanel();
    this.searchInput.nativeElement.blur();
    this.searchInput.nativeElement.value = '';
  }
}
